<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import contactCrmAPI from 'dashboard/api/contactCrm';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  addresses: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['refresh']);

// State
const isLoading = ref(false);
const showForm = ref(false);
const editingAddress = ref(null);

const formData = ref({
  address_type: 'residential',
  is_primary: false,
  label: '',
  postal_code: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  country: 'Brasil',
});

const addressTypeOptions = [
  { value: 'residential', label: 'Residencial', icon: 'i-lucide-home' },
  { value: 'commercial', label: 'Comercial', icon: 'i-lucide-building-2' },
  { value: 'delivery', label: 'Entrega', icon: 'i-lucide-truck' },
  { value: 'billing', label: 'Cobrança', icon: 'i-lucide-receipt' },
  { value: 'other', label: 'Outro', icon: 'i-lucide-map-pin' },
];

const stateOptions = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

// Methods
const resetForm = () => {
  formData.value = {
    address_type: 'residential',
    is_primary: false,
    label: '',
    postal_code: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    country: 'Brasil',
  };
  editingAddress.value = null;
};

const openForm = (address = null) => {
  if (address) {
    editingAddress.value = address;
    formData.value = { ...address };
  } else {
    resetForm();
  }
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const handleSave = async () => {
  isLoading.value = true;
  try {
    if (editingAddress.value) {
      await contactCrmAPI.updateAddress(
        props.contactId,
        editingAddress.value.id,
        formData.value
      );
      useAlert('Endereço atualizado com sucesso!');
    } else {
      await contactCrmAPI.createAddress(props.contactId, formData.value);
      useAlert('Endereço criado com sucesso!');
    }
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao salvar endereço');
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async address => {
  if (!confirm('Tem certeza que deseja excluir este endereço?')) return;

  isLoading.value = true;
  try {
    await contactCrmAPI.deleteAddress(props.contactId, address.id);
    useAlert('Endereço excluído com sucesso!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir endereço');
  } finally {
    isLoading.value = false;
  }
};

const handleSetPrimary = async address => {
  isLoading.value = true;
  try {
    await contactCrmAPI.setPrimaryAddress(props.contactId, address.id);
    useAlert('Endereço definido como principal!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao definir endereço principal');
  } finally {
    isLoading.value = false;
  }
};

const getAddressTypeIcon = type => {
  const opt = addressTypeOptions.find(o => o.value === type);
  return opt?.icon || 'i-lucide-map-pin';
};

const getAddressTypeLabel = type => {
  const opt = addressTypeOptions.find(o => o.value === type);
  return opt?.label || type;
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        Endereços ({{ addresses.length }})
      </h3>
      <Button
        label="Novo Endereço"
        icon="i-lucide-plus"
        size="sm"
        @click="openForm()"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="addresses.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-map-pin size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhum endereço cadastrado</p>
      <Button
        label="Adicionar primeiro endereço"
        variant="smooth"
        size="sm"
        class="mt-4"
        @click="openForm()"
      />
    </div>

    <!-- Address List -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div
        v-for="address in addresses"
        :key="address.id"
        class="flex flex-col p-4 rounded-xl bg-n-solid-2 outline outline-1 transition-all"
        :class="
          address.is_primary
            ? 'outline-woot-500 ring-1 ring-woot-500/20'
            : 'outline-n-container'
        "
      >
        <!-- Header Row -->
        <div class="flex items-start justify-between gap-3">
          <div class="flex items-center gap-3">
            <div
              class="flex-shrink-0 size-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span
                :class="getAddressTypeIcon(address.address_type)"
                class="size-5 text-n-slate-11"
              />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ address.label || getAddressTypeLabel(address.address_type) }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ getAddressTypeLabel(address.address_type) }}
              </p>
            </div>
          </div>
          <span
            v-if="address.is_primary"
            class="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-woot-500 text-white"
          >
            Principal
          </span>
        </div>

        <!-- Address Content -->
        <div class="mt-3 text-sm text-n-slate-11 space-y-0.5">
          <p v-if="address.street">
            {{ address.street }}{{ address.number ? `, ${address.number}` : '' }}
          </p>
          <p v-if="address.complement">{{ address.complement }}</p>
          <p v-if="address.neighborhood">{{ address.neighborhood }}</p>
          <p v-if="address.city || address.state">
            {{ address.city }}{{ address.state ? ` - ${address.state}` : '' }}
          </p>
          <p v-if="address.postal_code" class="text-n-slate-10">
            CEP: {{ address.postal_code }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 mt-4 pt-3 border-t border-n-weak">
          <button
            v-if="!address.is_primary"
            class="px-2.5 py-1 text-xs font-medium text-woot-600 hover:bg-woot-500/10 rounded-lg transition-colors"
            @click="handleSetPrimary(address)"
          >
            Definir como principal
          </button>
          <button
            class="px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors"
            @click="openForm(address)"
          >
            Editar
          </button>
          <button
            class="px-2.5 py-1 text-xs font-medium text-ruby-600 hover:bg-ruby-500/10 rounded-lg transition-colors"
            @click="handleDelete(address)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />

        <!-- Modal -->
        <div
          class="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ editingAddress ? 'Editar Endereço' : 'Novo Endereço' }}
            </h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-6">
            <div class="flex flex-col gap-4">
              <!-- Type & Label -->
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Tipo</label>
                  <select
                    v-model="formData.address_type"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  >
                    <option
                      v-for="opt in addressTypeOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Rótulo</label>
                  <input
                    v-model="formData.label"
                    type="text"
                    placeholder="Ex: Casa, Trabalho"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <!-- CEP & Street -->
              <div class="grid grid-cols-3 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">CEP</label>
                  <input
                    v-model="formData.postal_code"
                    type="text"
                    placeholder="00000-000"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
                <div class="col-span-2 flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Logradouro</label>
                  <input
                    v-model="formData.street"
                    type="text"
                    placeholder="Rua, Avenida, etc."
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <!-- Number & Complement -->
              <div class="grid grid-cols-3 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Número</label>
                  <input
                    v-model="formData.number"
                    type="text"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
                <div class="col-span-2 flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Complemento</label>
                  <input
                    v-model="formData.complement"
                    type="text"
                    placeholder="Apto, Sala, Bloco"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <!-- Neighborhood & City -->
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Bairro</label>
                  <input
                    v-model="formData.neighborhood"
                    type="text"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Cidade</label>
                  <input
                    v-model="formData.city"
                    type="text"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <!-- State & Country -->
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">Estado</label>
                  <select
                    v-model="formData.state"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  >
                    <option value="">Selecione</option>
                    <option
                      v-for="state in stateOptions"
                      :key="state"
                      :value="state"
                    >
                      {{ state }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-11">País</label>
                  <input
                    v-model="formData.country"
                    type="text"
                    class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <!-- Primary Checkbox -->
              <label class="inline-flex items-center gap-2 cursor-pointer">
                <input
                  v-model="formData.is_primary"
                  type="checkbox"
                  class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 focus:ring-offset-0"
                />
                <span class="text-sm text-n-slate-12">
                  Definir como endereço principal
                </span>
              </label>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Salvando...' : 'Salvar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
